import React, { useCallback, useState } from 'react'
import useParams from 'core/hooks/useParams'
import useReactRouter from 'use-react-router'
import { routeManager } from 'core/utils/routes'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { createClusterHostGroup } from './actions'
import { Route } from 'core/plugins/route'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import ModalForm from 'core/elements/modal/ModalForm'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import TextField from 'core/components/validatedForm/TextField'
import ToggleSwitchField from 'core/components/validatedForm/ToggleSwitchField'
import PicklistField from 'core/components/validatedForm/DropdownField'
import Text from 'core/elements/Text'
import HintTooltip from 'core/elements/tooltip/HintTooltip'
import { CreateClusterHostGroup, RebalancingFrequency, RebalancingStrategy } from './model'
import RebalancingFrequencyPickList from './RebalancingFrequencyPickList'
import CpuModePicklist from './CpuModePicklist'
import { customValidator } from 'core/utils/fieldValidators'
import SelectableCard from 'core/components/SelectableCard'
import ApiClient from 'api-client/ApiClient'
import { trackEvent } from 'utils/tracking'
import SimpleLink from 'core/components/SimpleLink'
import ExternalLink from 'core/components/ExternalLink'
import Alert from 'core/components/Alert'
import { IHealthServiceDetailsPageTabs } from '../../networks/model'

const gpuModes = [
  { label: 'Passthrough', value: 'passthrough' },
  { label: 'vGPU', value: 'vgpu' },
]

const nameValidator = customValidator((value) => {
  if (!value) {
    return true
  }
  const regex = /^[a-z0-9]([-a-z0-9]*[a-z0-9])?(\\.[a-z0-9]([-a-z0-9]*[a-z0-9])?)*$/
  return value.match(regex)
}, `Cluster name must consist of lower case alphanumeric characters or '-', start with an alphabetic character, and end with an alphanumeric character (e.g. 'my-name', or 'abc-123')`)

const { nova, neutron } = ApiClient.getInstance()

export const useStyles = makeStyles<Theme>((theme) => ({
  header: {
    display: 'grid',
    gridTemplateColumns: '1fr 21fr',
    gap: theme.spacing(2),
    alignItems: 'center',
    marginBottom: theme.spacing(4),
  },
  toggleSwitch: {
    marginTop: theme.spacing(2),
  },
  customFormFieldSection: {
    '& .content': {
      marginLeft: theme.spacing(0),
    },
  },
  customLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  customLabelTitle: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
  },
  questionCircle: {
    marginLeft: theme.spacing(1),
  },
  rebalancingFrequency: {
    marginLeft: theme.spacing(7),
  },
  gpuCards: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: 8,
  },
  card: {
    padding: 8,
  },
  gpuFields: {
    marginLeft: theme.spacing(7),
    display: 'grid',
    gap: 16,
  },
}))

interface Props {
  addRoute: Route
}

const CLUSTER_CREATION_FAILED_DOC =
  'https://platform9.com/docs/private-cloud-director/kb/unable-to-create-virtualized-cluster'

export default function CreateClusterHostGroupsModal({ addRoute }: Props) {
  const classes = useStyles()
  const { history } = useReactRouter()
  const [healthCheckFailed, setHealthCheckFailed] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const defaultParams = {
    name: '',
    vmHighAvailability: false,
    autoResourceRebalancing: false,
    enableGpu: false,
    rebalancingFrequency: RebalancingFrequency.Default,
    gpuType: 'passthrough',
    cpuMode: 'default',
    cpuModel: '',
    // metadata: [],
  }
  const { params, getParamsUpdater, updateParams, setParams } = useParams(defaultParams)
  const { update, updating, error, reset } = useUpdateAction(createClusterHostGroup)

  const handleClose = useCallback(() => {
    setParams(defaultParams)
    setHealthCheckFailed(null)
    reset()
    history.push(routeManager.routes.openstack.clusters.path())
  }, [setParams, reset, history])

  const checkServicesHealth = async () => {
    setHealthCheckFailed(null)
    let novaOK = false
    try {
      await nova.checkHealth()
      novaOK = true
      await neutron.checkHealth()
      return true
    } catch (e) {
      setHealthCheckFailed({
        title: 'Cluster creation failed',
        message: (
          <Text variant="body2">
            {!novaOK ? 'Compute' : 'Networking'} service is currently unhealthy. Please check the{' '}
            <SimpleLink
              src={routeManager.routes.openstack.health.details.path({
                tab: IHealthServiceDetailsPageTabs.ControlPlaneServicesHealth,
              })}
            >
              <strong>Service Health Dashboard</strong>
            </SimpleLink>{' '}
            and try again in sometime once the service is healthy.{' '}
            <ExternalLink url={CLUSTER_CREATION_FAILED_DOC}>
              <strong>More Info</strong>
            </ExternalLink>
          </Text>
        ),
      })
      return false
    }
  }

  const submitForm = useCallback(async () => {
    setSubmitting(true)
    const heathCheckOk = await checkServicesHealth()
    if (!heathCheckOk) {
      setSubmitting(false)
      return
    }

    const body: CreateClusterHostGroup = {
      name: params.name,
      vmHighAvailability: { enabled: params.vmHighAvailability },
      autoResourceRebalancing: {
        enabled: params.autoResourceRebalancing,
        ...(params.autoResourceRebalancing && {
          rebalancingStrategy: RebalancingStrategy.VMWorkloadConsolidation,
          rebalancingFrequencyMins: params.rebalancingFrequency,
        }),
      },
      gpu: {
        enabled: params.enableGpu,
        mode: params.gpuType,
      },
      cpu: {
        mode: params.cpuMode === 'default' ? null : params.cpuMode,
        model:
          params.cpuMode === 'custom' ? params.cpuModel : params.cpuMode === 'default' ? null : '',
      },
    }
    const { success } = await update({ body })
    if (success) {
      trackEvent(`Add Cluster - Finished`)
      handleClose()
    }
    setSubmitting(false)
  }, [params, update, handleClose])

  return (
    <ModalForm
      route={addRoute}
      title={`Add Cluster`}
      onSubmit={submitForm}
      onClose={handleClose}
      submitting={updating || submitting}
      error={error}
      initialValues={defaultParams}
      submitTitle={`Add Cluster`}
    >
      <>
        {healthCheckFailed && (
          <>
            <Alert variant="error" title={healthCheckFailed?.title}>
              {healthCheckFailed?.message}
            </Alert>
            <br />
          </>
        )}
        <div className={classes.header}>
          <img
            alt="host group"
            src="/ui/images/host-group.svg"
            data-testid="create-cluster-host-groups-modal--img-0"
          />
          <Text variant="body2">
            Create a new cluster to organize and manage hosts for workload deployment.
          </Text>
        </div>
        <FormFieldSection title="Cluster Configuration" className={classes.customFormFieldSection}>
          <TextField
            id="name"
            label="Name"
            onChange={getParamsUpdater('name')}
            value={params.name}
            validations={[nameValidator]}
            required
            info={`Cluster name must consist of lower case alphanumeric characters or '-', start with an alphabetic character, and end with an alphanumeric character (e.g. 'my-name', or 'abc-123')`}
          />
          <PicklistField
            DropdownComponent={CpuModePicklist}
            id="cpuMode"
            onChange={getParamsUpdater('cpuMode')}
            value={params.cpuMode}
            label="CPU Mode"
            info={
              <div>
                <div>Controls how the hypervisor exposes CPU features to VMs.</div>
                <br />
                <div>Default: Select the latest supported CPU model on the host.</div>
                <br />
                <div>
                  Custom: Specify a standardized CPU model to enable live migration between hosts
                  with different physical CPUs.
                </div>
                <br />
                <div>
                  Host Model: Closely matches the physical CPU for balanced performance and
                  migration flexibility.
                </div>
                <br />
                <div>
                  Host Passthrough: Directly exposes the physical CPU for maximum performance but
                  limits migration to identical hardware.
                </div>
              </div>
            }
          />
          {params.cpuMode === 'custom' && (
            <TextField
              id="cpuModel"
              label="CPU Model"
              onChange={getParamsUpdater('cpuModel')}
              value={params.cpuModel}
              placeholder="eg. Nehalem"
              required
              info="The CPU model that the virtualization layer (hypervisor) exposes to virtual machines (VMs). Used to standardize CPU features across hosts on this cluster to enable live migration between hosts. This may differ from the physical host CPU model."
            />
          )}
          <div className={classes.toggleSwitch}>
            <ToggleSwitchField
              id="vmHighAvailability"
              label="VM High Availability"
              value={params.vmHighAvailability}
              onChange={(value) => getParamsUpdater('vmHighAvailability')(value)}
              customLabel={
                <div className={classes.customLabel}>
                  <Text variant="caption1" className={classes.customLabelTitle}>
                    VM High Availability
                    <HintTooltip message="Automatically detect host failure and recover VMs on other host group hosts." />
                  </Text>
                  <Text variant="body2">
                    This feature requires at least 2 hosts that are authorized as hypervisors.
                  </Text>
                </div>
              }
            />
          </div>
          <div className={classes.toggleSwitch}>
            <ToggleSwitchField
              id="autoResourceRebalancing"
              label="Dynamic Resource Rebalancing"
              value={params.autoResourceRebalancing}
              onChange={(value) => getParamsUpdater('autoResourceRebalancing')(value)}
              customLabel={
                <div className={classes.customLabel}>
                  <Text variant="caption1" className={classes.customLabelTitle}>
                    Dynamic Resource Rebalancing
                    <HintTooltip message="Dynamically rebalances workloads across hosts in the host group to optimize resource utilization and prevent overload." />
                  </Text>
                  <Text variant="body2">
                    Dynamically rebalance workloads across hosts in the cluster.
                  </Text>
                </div>
              }
            />
          </div>
          {params.autoResourceRebalancing && (
            <RebalancingFrequencyPickList
              value={params.rebalancingFrequency}
              onChange={(value) => getParamsUpdater('rebalancingFrequency')(value)}
              className={classes.rebalancingFrequency}
            />
          )}
          <div className={classes.toggleSwitch}>
            <ToggleSwitchField
              id="enableGpu"
              label="GPU"
              value={params.enableGpu}
              onChange={(value) => getParamsUpdater('enableGpu')(value)}
              customLabel={
                <div className={classes.customLabel}>
                  <Text variant="caption1" className={classes.customLabelTitle}>
                    GPU
                    <HintTooltip message="Enable GPU support for this cluster to allow GPU passthrough, or vGPU-based virtual machines. This must be turned on before GPU configuration or assignment." />
                  </Text>
                  <Text variant="body2">Enable running GPU workloads on the cluster.</Text>
                </div>
              }
            />
          </div>
          {params.enableGpu && (
            <div className={classes.gpuFields}>
              <Text variant="subtitle2">GPU Mode</Text>
              <Text variant="body2">
                Choose the type of GPU support you want this flavor to provide. Use{' '}
                <b>Passthrough</b> to assign full physical GPUs, or <b>vGPU</b> to use virtual GPU
                slices.
              </Text>
              <div className={classes.gpuCards}>
                {gpuModes.map(({ label, value }) => (
                  <SelectableCard
                    id={value}
                    onClick={() => updateParams({ gpuType: value })}
                    active={params.gpuType === value}
                    withCustomBody
                    showRadioButton
                    className={classes.card}
                  >
                    <Text variant="caption1">{label}</Text>
                  </SelectableCard>
                ))}
              </div>
            </div>
          )}
          {/* @TODO: API does not support metadata. Commenting out for now. https://platform9.atlassian.net/browse/PCD-1508 */}
          {/* <KeyValuesField
            id="metadata"
            label="Metadata"
            value={params.metadata}
            onChange={getParamsUpdater('metadata')}
            addLabel="Add Metadata"
            allowMultipleValues
          /> */}
        </FormFieldSection>
      </>
    </ModalForm>
  )
}
